import { Trans } from "react-i18next";
import { t } from "i18next";
import type { ChangeEvent } from "react";
import { Input } from "antd";
import { useSetState } from "ahooks";
import dayjs from "dayjs";
import type { LinkedAccount } from "@store/linkedAccountSlice";
import { ProductPicker } from "./ProductPicker";
import { twMerge } from "tailwind-merge";
import { ClipSwitch } from "@components/ClipSwitch";
import { UserPublishSubmitRequestPublishMode } from "@apis/generated/openapi";
import { UserPublishSubmitRequest } from "@apis/http/api";
import { ScheduledDate } from "./ScheduledDate";
import { AccountSelector } from "./AccountSelector";
import { REGION_TIMEZONE_MAP, DEFAULT_TIMEZONE } from "@src/config/timezone";
import { TestId } from "@shared/testIds";

export interface PostToTikTokFormState extends Pick<
  UserPublishSubmitRequest,
  "needPrecheck" | "publishTime" | "publishMode"
> {
  /** video description */
  description: string;
  /** Short Product Title */
  title: string;
  productId: string;
  productTitle: string;
  productImage: string;
  salesCount?: number;
  /** 发布记录 id */
  postRecordId: number | undefined;
}

export interface FormBodyProps {
  className?: string;
  accounts: LinkedAccount[];
  selectedLinkId: number;
  selectedAccount?: LinkedAccount;
  onSelectAccount: (linkId: number) => void;
  formState: PostToTikTokFormState;
  setFormState: ReturnType<typeof useSetState<PostToTikTokFormState>>[1];
  disabled?: boolean;
  productDisabled?: boolean;
  scheduledDateError?: boolean;
  titleError?: boolean;
  titleBannedCharsDisplay?: string;
  onTitleBlur?: () => void;
}

export const FormBody = (props: FormBodyProps) => {
  const {
    accounts,
    selectedLinkId,
    selectedAccount,
    onSelectAccount,
    formState,
    setFormState,
    disabled,
    productDisabled,
    className,
    scheduledDateError,
    titleError,
    titleBannedCharsDisplay,
    onTitleBlur,
  } = props;
  const { needPrecheck } = formState;

  const targetTimezone = selectedAccount?.region
    ? REGION_TIMEZONE_MAP[selectedAccount.region] || DEFAULT_TIMEZONE
    : DEFAULT_TIMEZONE;

  const labelClassName = "flex h-[30px] items-center text-[16px] font-bold leading-[22px] text-kds-fg";

  return (
    <div className={twMerge("flex flex-col", className)}>
      <div className="flex flex-col gap-[16px] px-[24px] pt-[8px] pb-[24px]">
        <div className="flex flex-col gap-[4px]">
          <label className={labelClassName}>
            <Trans>Select Account</Trans>
            <span className="ml-[2px] text-kds-danger">*</span>
          </label>
          <AccountSelector accounts={accounts} value={selectedLinkId} onChange={onSelectAccount} disabled={disabled} />
        </div>

        <div className="flex flex-col gap-[4px]">
          <label className={labelClassName}>
            <Trans>Video Description</Trans>
            <span className="ml-[2px] text-kds-danger">*</span>
          </label>
          <Input.TextArea
            data-testid={TestId.PostToTikTokDescription}
            showCount
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder={t("e.g., Sharing a product I've been loving lately")}
            maxLength={4000}
            value={formState.description}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormState({ description: e.target.value })}
            styles={{
              count: {
                bottom: 8,
                right: 12,
                fontSize: 12,
                color: "var(--ant-color-text-placeholder)",
              },
            }}
          />
          <div className="text-[12px] leading-[16px] text-kds-fg-subtle">{t("Type # to add hashtags")}</div>
        </div>

        <div className="h-px w-full bg-kds-border" aria-hidden />

        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[4px]">
            <label className={labelClassName}>
              <Trans>Product to Promote</Trans>
              <span className="ml-[2px] text-kds-danger">*</span>
            </label>
            <ProductPicker
              linkId={selectedLinkId}
              value={formState}
              onChange={(next) => {
                const { productId, productTitle, productImage, salesCount } = next;
                setFormState({
                  productId,
                  productTitle,
                  productImage,
                  salesCount,
                });
              }}
              disabled={disabled || productDisabled}
            />
          </div>

          <div className="flex flex-col gap-[4px]">
            <label className={labelClassName}>
              <Trans>Short Product Title</Trans>
            </label>
            <Input
              data-testid={TestId.PostToTikTokShortTitle}
              placeholder={t("e.g., 100% Cotton Short-Sleeve T-shirt")}
              maxLength={30}
              value={formState.title}
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFormState({ title: e.target.value })}
              onBlur={onTitleBlur}
            />
            {titleError ? (
              <div
                data-testid={TestId.PostToTikTokTitleBannedError}
                className="text-kds-danger text-[12px] leading-[16px] font-normal"
              >
                {t("Cannot contain: {{ban_characters}}", {
                  ban_characters: titleBannedCharsDisplay ?? "",
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="h-px w-full bg-kds-border" aria-hidden />

        <div className="flex flex-col gap-[16px]">
          <ClipSwitch
            checked={needPrecheck}
            onChange={(nextValue) => setFormState({ needPrecheck: nextValue })}
            title={t("Video Pre-check")}
            description={t("Use TikTok’s official video compliance check to prevent account issues")}
          />

          <ScheduledDate
            disabled={disabled}
            error={scheduledDateError}
            errorText="Please select a publish time."
            targetTimezone={targetTimezone}
            value={{
              scheduled: formState.publishMode === UserPublishSubmitRequestPublishMode.SCHEDULE,
              time: formState.publishTime ? dayjs(formState.publishTime) : undefined,
            }}
            onChange={(nextValue) => {
              if (!nextValue.scheduled) {
                setFormState({
                  publishMode: UserPublishSubmitRequestPublishMode.NOW,
                  publishTime: undefined,
                });
                return;
              }
              setFormState({
                publishMode: UserPublishSubmitRequestPublishMode.SCHEDULE,
                publishTime: nextValue.time?.valueOf(),
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
